import { NgTemplateOutlet } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
  afterNextRender,
  Component,
  computed,
  contentChildren,
  DOCUMENT,
  HostListener,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import type { SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';

import { WINDOW } from '@rpgc-ai/shared-interfaces/tokens/window.token';

import { SelectTagComponent } from '../select/components/tag/select-tag.component';

const ZERO_WIDTH_SPACE = '\u200B';
const INVISIBLE_CHARS_REGEX = /[\u200B-\u200D\uFEFF\u00A0\s]/g;

type DocumentWithCaretRangeApi = Document & {
  caretRangeFromPoint?: (x: number, y: number) => Range | null;
};

@Component({
  imports: [FormsModule, NgTemplateOutlet],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: EditableDivComponent,
      multi: true,
    },
  ],
  selector: 'app-ui-editable-div',
  styleUrl: './styles/editable-div.component.scss',
  templateUrl: './editable-div.component.html',
})
export class EditableDivComponent implements ControlValueAccessor {
  readonly #document = inject(DOCUMENT);
  readonly #window = inject(WINDOW);
  readonly #sanitizer = inject(DomSanitizer);
  #dragSelectionRange: Range | null = null;

  readonly placeholder = input<string>('');
  readonly isPlaceholderVisible = input<boolean | undefined>();
  readonly title = input<string>('');
  readonly elementId = input<string>('');
  readonly readonly = input<boolean>(false);
  readonly autocomplete = input<string>('on');
  readonly maxLines = input<number>(15);
  readonly isRequired = input<boolean>(false);
  readonly errorMessage = input<string>('');
  readonly copyable = input<boolean>(false);
  readonly allowDefaultEnterEvent = input<boolean>(true);
  readonly expandOnEnter = input<boolean>(true);
  readonly showErrorOnDemand = input<boolean>(false);

  readonly focusEvent = output<FocusEvent>();
  readonly blurEvent = output<FocusEvent>();
  readonly inputEvent = output<Event>();
  readonly tagInserted = output<{ tag: string; position: number }>();
  readonly readValue = output();
  readonly keydownEvent = output<KeyboardEvent>();

  readonly value = model<string | null>(null);
  readonly disabled = model<boolean>(false);

  readonly editableDiv = viewChild<ElementRef>('editableDiv');
  readonly projectedTags = contentChildren(SelectTagComponent, { descendants: true });

  readonly isFocused = signal<boolean>(false);
  readonly preventFocusBehavior = signal<boolean>(false);
  readonly showCopyNotification = signal<boolean>(false);
  readonly hasError = signal<boolean>(false);

  readonly labelId = computed<string>(() => this.elementId() || `editable-div-${Math.random().toString(36).substring(2, 9)}`);
  readonly formattedPlaceholder = computed<SafeHtml>(() => {
    const placeholderText = this.placeholder();
    if (!placeholderText) {
      return this.#sanitizer.bypassSecurityTrustHtml('');
    }
    // Преобразуем переносы строк в <br> для корректного отображения в innerHTML
    const htmlContent = placeholderText.replace(/\n/g, '<br>');
    return this.#sanitizer.bypassSecurityTrustHtml(htmlContent);
  });

  constructor() {
    // Используем afterNextRender для работы с DOM только в браузерном окружении
    afterNextRender(() => {
      this.#initializeComponent();
    });
  }

  /**
   * Обработчик нажатия клавиш для управления многострочностью
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Если компонент отключен или только для чтения, не обрабатываем события
    if (this.disabled() || this.readonly()) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey && !this.allowDefaultEnterEvent()) {
      event.preventDefault();
      this.readValue.emit();
    }

    // Если нажат Enter и не зажат Shift, предотвращаем стандартное поведение
    // чтобы избежать создания нового параграфа (что может нарушить стили)
    if (event.key === 'Enter' && (event.shiftKey || this.allowDefaultEnterEvent())) {
      event.preventDefault();

      if (!this.expandOnEnter()) {
        return;
      }

      // Вставляем перенос строки
      const selection = this.#document.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        // Создаем фрагмент с <br> и пустым текстовым узлом
        const fragment = this.#document.createDocumentFragment();
        const br = this.#document.createElement('br');
        // Добавляем невидимый пробел после <br> для корректного позиционирования курсора
        const textNode = this.#document.createTextNode(ZERO_WIDTH_SPACE); // Добавляем невидимый пробел

        fragment.appendChild(br);
        fragment.appendChild(textNode);

        range.deleteContents();
        range.insertNode(fragment);

        // Вычисляем позицию курсора после вставленного текстового узла
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);

        // Вызываем обработчик input для обновления модели
        this.onInput(new Event('input'));
      }
    }
  }

  setFocusOnInput(): void {
    const element = this.editableDiv()?.nativeElement;

    if (!element) {
      return;
    }

    element.focus();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    if (this.disabled() || this.readonly()) {
      return;
    }

    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!text) {
      this.#ensureCaretInsideEditableDiv();
      return;
    }

    this.#insertPlainText(text);
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.#dragSelectionRange ? 'move' : 'copy';
    }
  }

  onDragStart(event: DragEvent): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    const selection = this.#document.getSelection();
    if (!selection || selection.rangeCount === 0) {
      this.#dragSelectionRange = null;
      return;
    }

    const range = selection.getRangeAt(0);
    if (range.collapsed || !this.#isRangeInsideEditableDiv(range)) {
      this.#dragSelectionRange = null;
      return;
    }

    this.#dragSelectionRange = range.cloneRange();

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copyMove';
    }
  }

  onDragEnd(): void {
    this.#dragSelectionRange = null;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    if (this.disabled() || this.readonly()) {
      return;
    }

    const dropRange = this.#getDropRangeAtPoint(event.clientX, event.clientY);
    const selection = this.#document.getSelection();
    if (dropRange && selection && this.#isRangeInsideEditableDiv(dropRange)) {
      selection.removeAllRanges();
      selection.addRange(dropRange);
    }

    const text = event.dataTransfer?.getData('text/plain') ?? '';
    if (!text) {
      this.#ensureCaretInsideEditableDiv();
      this.#dragSelectionRange = null;
      return;
    }

    if (this.#dragSelectionRange && this.#isRangeInsideEditableDiv(this.#dragSelectionRange)) {
      this.#dragSelectionRange.deleteContents();
    }

    this.#insertPlainText(text);
    this.#dragSelectionRange = null;
  }

  /**
   * Вставляет парные теги в текущую позицию курсора и устанавливает курсор между ними
   * @param tag Тег для вставки (например, '*' для курсива)
   * @returns Позиция курсора после вставки
   */
  insertPairedTags(tags: string[]): number {
    // Если компонент отключен или только для чтения, не выполняем вставку
    if (this.disabled() || this.readonly()) {
      return -1;
    }

    const editableDivRef = this.editableDiv();
    if (!editableDivRef) {
      return -1;
    }

    const element = editableDivRef.nativeElement;
    //prevent focus behavior on mobile devices
    this.preventFocusBehavior.set(true);
    element.focus(); // Фокусируемся на элементе перед вставкой

    const selection = this.#document.getSelection();
    if (!selection) {
      return -1;
    }

    // Создаем диапазон, если его нет
    if (selection.rangeCount === 0) {
      const newRange = this.#document.createRange();
      newRange.selectNodeContents(element);
      newRange.collapse(false); // Коллапсируем в конец
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    const range = selection.getRangeAt(0);

    if (!range.collapsed) {
      const selectedText = range.toString();
      tags.splice(1, 0, selectedText);
      // Remove selected text before insert tags
      range.deleteContents();
    }
    this.#insertTextIntoInput(range, selection, tags);

    // Обновляем модель
    this.onInput(new Event('input'));

    // Генерируем событие вставки тега
    this.tagInserted.emit({ tag: tags[0], position: range.startOffset });

    return range.startOffset;
  }

  /**
   * Реализация интерфейса ControlValueAccessor
   */
  writeValue(value: string | null): void {
    this.value.set(value);

    // Обновляем HTML содержимое, если элемент уже инициализирован
    const editableDivRef = this.editableDiv();
    if (editableDivRef) {
      if (value) {
        this.#setTextContent(editableDivRef.nativeElement, value);
      } else {
        editableDivRef.nativeElement.innerHTML = '';
      }
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  /**
   * Обработчик события input
   */
  onInput(event: Event): void {
    const editableDivRef = this.editableDiv();
    if (!editableDivRef) {
      return;
    }

    // Получаем HTML содержимое
    const htmlContent = editableDivRef.nativeElement.innerHTML || '';

    // Проверяем, содержит ли HTML только невидимые символы или пустые теги
    const isEmptyContent = this.#isContentEmpty(htmlContent);

    if (isEmptyContent) {
      // Если содержимое пустое или содержит только невидимые символы, очищаем его
      editableDivRef.nativeElement.innerHTML = '';
      this.value.set(null);
      this.#onChange(null);
    } else {
      // Преобразуем HTML в текст с переносами строк
      const textContent = this.#htmlToText(htmlContent);
      this.value.set(textContent);
      this.#onChange(textContent);
    }

    this.inputEvent.emit(event);
  }

  /**
   * Обработчик события focus
   */
  onFocus(event: FocusEvent): void {
    this.isFocused.set(true);
    this.focusEvent.emit(event);

    //Sets the caret to the end of the content on mobile to cope with "Next" btn behavior
    if (this.#isMobile() && !this.preventFocusBehavior()) {
      this.setCaretToEnd();
    }

    this.preventFocusBehavior.set(false);
  }

  /**
   * Обработчик события blur
   */
  onBlur(event: FocusEvent): void {
    this.isFocused.set(false);
    this.#onTouched();
    this.blurEvent.emit(event);
    this.#validate();
  }

  setCaretToEnd(): void {
    const nativeElement = this.editableDiv()?.nativeElement;
    if (!nativeElement) {
      return;
    }

    const range = this.#document.createRange();
    range.selectNodeContents(nativeElement);
    range.collapse(false);

    const selection = this.#document.getSelection();
    if (!selection) {
      return;
    }
    selection.removeAllRanges();
    selection.addRange(range);
  }

  #insertPlainText(text: string): void {
    this.#ensureCaretInsideEditableDiv();

    const selection = this.#document.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    const fragment = this.#createTextFragment(text);
    const lastNode = fragment.lastChild;

    range.deleteContents();
    range.insertNode(fragment);

    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
    }

    selection.removeAllRanges();
    selection.addRange(range);

    this.onInput(new Event('input'));
  }

  #ensureCaretInsideEditableDiv(): void {
    const editableDivRef = this.editableDiv();
    if (!editableDivRef) {
      return;
    }

    const element = editableDivRef.nativeElement;
    element.focus();

    const selection = this.#document.getSelection();
    if (!selection) {
      return;
    }

    const hasValidRange = selection.rangeCount > 0 && !!selection.anchorNode && element.contains(selection.anchorNode);

    if (hasValidRange) {
      return;
    }

    const range = this.#document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  #getDropRangeAtPoint(clientX: number, clientY: number): Range | null {
    const documentWithCaretRangeApi = this.#document as DocumentWithCaretRangeApi;
    const rangeFromPoint = documentWithCaretRangeApi.caretRangeFromPoint?.(clientX, clientY);
    if (rangeFromPoint) {
      return rangeFromPoint;
    }

    const positionFromPoint = documentWithCaretRangeApi.caretPositionFromPoint?.(clientX, clientY);
    if (!positionFromPoint) {
      return null;
    }

    const range = this.#document.createRange();
    range.setStart(positionFromPoint.offsetNode, positionFromPoint.offset);
    range.collapse(true);
    return range;
  }

  #isRangeInsideEditableDiv(range: Range): boolean {
    const editableDivRef = this.editableDiv();
    if (!editableDivRef) {
      return false;
    }

    const element = editableDivRef.nativeElement;
    return element.contains(range.startContainer);
  }

  #insertTextIntoInput(range: Range, selection: Selection, text: string[], customFragment?: DocumentFragment): void {
    // Если нет выделения, вставляем парные теги и ставим курсор между ними
    // Создаем фрагмент с первым тегом, невидимым маркером и вторым тегом
    const fragment = customFragment ?? this.#document.createDocumentFragment();
    //store future nodes
    const textNodes: Text[] = [];

    text.forEach((t) => {
      const textRef = this.#document.createTextNode(t);
      fragment.appendChild(textRef);
      textNodes.push(textRef);
    });

    // Вставляем фрагмент в текущую позицию
    range.insertNode(fragment);

    // Устанавливаем курсор после маркера (между тегами)
    range.setStartAfter(textNodes[0]);
    range.setEndAfter(textNodes[0]);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /**
   * Валидирует компонент
   */
  #validate(): void {
    if (this.isRequired() && !this.value()) {
      this.hasError.set(true);
    } else {
      this.hasError.set(false);
    }
  }

  /**
   * Инициализация компонента - выполняется только в браузере благодаря afterNextRender
   */
  #initializeComponent(): void {
    const editableDivRef = this.editableDiv();
    if (!editableDivRef) {
      return;
    }

    const element = editableDivRef.nativeElement;

    // Установка начального значения
    const currentValue = this.value();
    if (currentValue) {
      this.#setTextContent(element, currentValue);
    } else {
      element.textContent = '';
    }
  }

  #onChange = (_: string | null): void => {
    // Обработчик изменения значения
  };

  #onTouched = (): void => {
    // Обработчик потери фокуса
  };

  #isMobile(): boolean {
    return /Mobi|Android|iPhone|iPad/i.test(this.#window?.navigator.userAgent ?? '');
  }

  /**
   * Проверяет, является ли содержимое пустым или содержащим только невидимые символы
   * @param html HTML-содержимое для проверки
   * @returns true, если содержимое пустое или содержит только невидимые символы
   */
  #isContentEmpty(html: string): boolean {
    if (!html || html === '') {
      return true;
    }

    // Создаем временный div для парсинга HTML
    const tempDiv = this.#document.createElement('div');
    tempDiv.innerHTML = html;

    // Получаем текстовое содержимое
    const textContent = tempDiv.textContent || '';

    // Удаляем все невидимые символы (нулевой пробел, маркеры и т.д.)
    const visibleContent = textContent.replace(INVISIBLE_CHARS_REGEX, '');

    // Проверяем, есть ли видимые символы
    return visibleContent.length === 0;
  }

  /**
   * Преобразует HTML с тегами <br> в текст с переносами строк
   */
  #htmlToText(html: string): string {
    if (!html) {
      return '';
    }

    // Создаем временный div для парсинга HTML
    const tempDiv = this.#document.createElement('div');
    tempDiv.innerHTML = html;

    // Заменяем все <br> на символы переноса строки
    const brElements = tempDiv.querySelectorAll('br');
    brElements.forEach((br) => {
      const textNode = this.#document.createTextNode('\n');
      br.parentNode?.insertBefore(textNode, br);
      br.parentNode?.removeChild(br);
    });

    // Возвращаем текстовое содержимое без HTML-тегов
    return tempDiv.textContent || '';
  }

  #setTextContent(element: HTMLElement, text: string): void {
    element.innerHTML = '';
    const fragment = this.#createTextFragment(text);
    element.appendChild(fragment);
  }

  #createTextFragment(text: string): DocumentFragment {
    const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const fragment = this.#document.createDocumentFragment();
    const lines = normalizedText.split('\n');

    lines.forEach((line, index) => {
      const textNode = this.#document.createTextNode(line);
      fragment.appendChild(textNode);

      if (index < lines.length - 1) {
        const br = this.#document.createElement('br');
        fragment.appendChild(br);
      }
    });

    return fragment;
  }
}
